import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, ExternalLink, Bitcoin } from "lucide-react";
import Seo from "@/components/Seo";
import { show } from "@/lib/podcast";
import { siteConfig } from "@/config/siteConfig";
import SupportWidget from "@/components/SupportWidget";

const COINGECKO =
  "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd";

function BitcoinWidget() {
  const [state, setState] = useState({
    price: null,
    sats: null,
    updated: "",
    error: false,
  });

  useEffect(() => {
    let active = true;
    const update = async () => {
      try {
        const res = await fetch(COINGECKO);
        const data = await res.json();
        const price = data.bitcoin.usd;
        if (!active) return;
        setState({
          price,
          sats: Math.round(100000000 / price),
          updated: new Date().toLocaleTimeString([], {
            hour: "numeric",
            minute: "2-digit",
          }),
          error: false,
        });
      } catch {
        if (active) setState((s) => ({ ...s, error: true }));
      }
    };
    update();
    const iv = setInterval(update, 60000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, []);

  return (
    <div
      data-testid="bitcoin-widget"
      className="mx-auto my-6 max-w-xs rounded-md border border-[#0b1f4d]/10 bg-[#f3ecdc] p-5 text-center"
    >
      <p className="font-display uppercase text-sm font-semibold tracking-wide text-[#0b1f4d]">
        Current ₿ Bitcoin Price
      </p>
      <p className="mt-1 text-xs text-[#66718d]">
        Send sats instantly via the Lightning Network
      </p>
      {state.error ? (
        <p
          data-testid="btc-price"
          className="mt-4 text-lg font-semibold text-[#4d5a78]"
        >
          Price unavailable
        </p>
      ) : (
        <>
          <span
            data-testid="btc-price"
            className="mt-4 block text-3xl font-bold text-[#9a6700] font-mono"
          >
            {state.price ? `$${state.price.toLocaleString()}` : "Loading…"}
          </span>
          {state.sats && (
            <span className="mt-2 block text-sm font-semibold text-[#374566]">
              {state.sats.toLocaleString()} sats per $1
            </span>
          )}
          {state.updated && (
            <span className="mt-2 block text-xs text-[#66718d]">
              Updated {state.updated}
            </span>
          )}
        </>
      )}
    </div>
  );
}

export default function Support() {
  return (
    <div data-testid="support-page">
      <Seo
        title="Support"
        description={`Support ${show.title}. ${siteConfig.support.blurb}`}
        image={show.image}
        path="/support"
      />

      {/* Hero */}
      <section className="hero-bg border-b border-[#0b1f4d]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9a6700]">
            Support the show
          </span>
          <h1 className="font-poster uppercase text-5xl sm:text-6xl text-[#0b1f4d] mt-2 tracking-tight">
            Support WVEdChat
          </h1>
          <p className="mt-3 text-lg text-[#8a5a00] font-display uppercase tracking-wide">
            Conversations Along the Road of West Virginia Education
          </p>
          <div className="mt-6 space-y-4 text-lg text-[#374566] leading-relaxed max-w-2xl">
            <p>
              WVEdChat is independently produced and listener supported.
              Your support helps cover podcast hosting, equipment, website costs,
              and the time required to research, record, edit, and publish each
              episode.
            </p>
            <p>
              If you've found value in the show, consider giving value back.
              Whether through a one-time gift, ongoing support, or a Boostagram,
              every contribution helps keep the conversation going.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-6">
        {/* Value 4 Value */}
        <div className="rounded-md border border-[#0b1f4d]/10 bg-white p-6 sm:p-8">
          <h2 className="font-display uppercase text-2xl font-semibold text-[#0b1f4d] text-center">
            Value 4 Value
          </h2>
          <div className="show-notes mt-5">
            <p>
              WVEdChat embraces the Value 4 Value model. Instead of
              relying on subscriptions or paywalls, the show is made freely
              available to everyone. If you receive value from an episode, you're
              invited to return value in whatever way makes sense for you.
            </p>
            <p>
              In the Value 4 Value philosophy, support often takes the form of
              the Three Ts: Time, Talent, and Treasure.
            </p>
            <ul>
              <li>
                <strong>Time</strong> — Listen to episodes, share them with
                friends, leave ratings and reviews, or recommend the podcast to
                others.
              </li>
              <li>
                <strong>Talent</strong> — Contribute your skills, ideas,
                feedback, expertise, or creative assistance that can help improve
                or grow the show.
              </li>
              <li>
                <strong>Treasure</strong> — Provide financial support through
                donations, Boostagrams, Bitcoin, or other contributions that help
                cover the costs of producing and hosting the podcast.
              </li>
            </ul>
            <p>
              Every form of support matters. Whether you give your time, your
              talent, your treasure, or some combination of all three, you help
              make WVEdChat possible.
            </p>
          </div>
        </div>

        {/* Boostagrams */}
        <div className="rounded-md border border-[#0b1f4d]/10 bg-white p-6 sm:p-8">
          <h2 className="font-display uppercase text-2xl font-semibold text-[#0b1f4d] text-center">
            Boostagrams
          </h2>
          <div className="show-notes mt-5">
            <p>
              Through Podcasting 2.0's Value for Value system, listeners can
              support WVEdChat directly from compatible podcast apps
              without relying on advertising or subscriptions. If you listen with
              a Podcasting 2.0 app, you can send a Boostagram while you listen. A
              Boostagram combines a Bitcoin payment with a message, allowing you
              to support the show, share feedback, and encourage the show in
              real time.
            </p>
            <p>
              Boostagrams are sent using sats (satoshis), the smallest unit of
              Bitcoin. Even small amounts can make a meaningful impact when
              combined with encouragement, sharing, or feedback about an episode.
            </p>
            <p>
              Listener messages, Boostagrams, and other forms of support may be
              recognized in future episodes as a way of thanking those who help
              make WVEdChat possible.
            </p>
            <p>
              Not all podcast apps support Boostagrams and other Podcasting 2.0
              features. To find a modern podcast app that does, visit
              PodcastApps.com and compare apps that support features such as
              Boostagrams, transcripts, chapters, and more.
            </p>
          </div>
          <div className="text-center mt-6">
            <a
              href="https://podcastapps.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="support-podcastapps-button"
              className="inline-flex items-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-bold uppercase tracking-wide px-6 py-3 transition-colors"
            >
              Find a Podcasting 2.0 App
            </a>
          </div>
        </div>

        {/* Support the Show — Stripe widget (shared podcast-support Worker) */}
        <div className="rounded-md border border-[#0b1f4d]/10 bg-white p-6 sm:p-8">
          <h2 className="font-display uppercase text-2xl font-semibold text-[#0b1f4d] text-center">
            Support the Show
          </h2>
          <p className="mt-3 text-[#4d5a78] leading-relaxed text-center max-w-2xl mx-auto">
            If WVEdChat has provided value to you, consider returning
            value with a one-time gift or recurring contribution — right here,
            without leaving the page.
          </p>
          <div className="mt-8">
            <SupportWidget apiBase={siteConfig.support.apiBase} theme={siteConfig.support.theme} />
          </div>
        </div>

        {/* Bitcoin / Strike — full-width row */}
        <div className="flex flex-col rounded-md border border-[#0b1f4d]/10 bg-white p-6 sm:p-8">
          <div className="h-11 w-11 grid place-items-center rounded-sm bg-[#f5b61c]/15 text-[#9a6700]">
            <Bitcoin size={20} />
          </div>
          <h2 className="mt-4 font-display uppercase text-2xl font-semibold text-[#0b1f4d]">
            Bitcoin via Strike
          </h2>
          <p className="mt-3 text-[#4d5a78] leading-relaxed">
            Support the show directly using Bitcoin through Strike.
          </p>

          <BitcoinWidget />

          <a
            href="https://strike.me/rgblack316/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-strike-button"
            className="mt-auto inline-flex items-center justify-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-bold uppercase tracking-wide px-6 py-3 transition-colors"
          >
            <Zap size={17} /> Support via Strike
          </a>
        </div>

        {/* Contact CTA */}
        <div className="text-center pt-4">
          <p className="text-[#374566] mb-4">
            Have questions, feedback, or another way you'd like to support the
            show?
          </p>
          <Link
            to="/contact"
            data-testid="support-contact-link"
            className="inline-flex items-center gap-2 rounded-sm border border-[#0b1f4d]/20 hover:border-[#f5b61c] text-[#0b1f4d] font-semibold uppercase tracking-wide px-6 py-3 transition-colors"
          >
            Contact Randy <ExternalLink size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
